/**
 * EDGE connector — pure derivations and product copy.
 *
 * Glossary: "service" = this connector's on-prem component, the program the
 * customer installs; NOT dev's edge-fleet device agent (edgeFleet.ts, EdgeFleetTab).
 *
 * An EDGE connector is an agent installed on the customer's own server: it
 * watches a folder and PUSHES to PAI. PAI never dials into the customer
 * network, so everything this module renders comes from what the agent last
 * reported.
 *
 * Everything here is pure and free of React so both the card and the detail
 * panel derive the SAME sentence from the same timestamp. Two renderers for
 * one field is how a card and a panel end up disagreeing about it.
 *
 * Copy is neutral Spanish with `tú` (AGENTS.md product-copy rule) and is
 * deliberately generic: no share paths, no protocol names, no client names.
 * `source_status` is an opaque string the agent chooses; PAI only displays it.
 */
import type { EdgeAgentState, EdgeFetchView, EdgeFileEvent, EdgeFileEventType } from '../../types/connector';

/** Catalog type string. Stored on the connector row and matched everywhere. */
export const EDGE_TYPE = 'EDGE';

/**
 * The source every file of an agent that predates sources lives in. Its paths
 * display exactly as they always did; any other source is named in front.
 */
export const EDGE_DEFAULT_SOURCE_ID = 'default';

/**
 * The `StatusDot` variants liveness is allowed to use.
 *
 * `error` is deliberately absent: an agent that has not reported is not a
 * connector in error, and reusing that variant would make the liveness badge
 * indistinguishable from the connector's own `status` field (idle/active/error),
 * which means something else entirely for the other 30+ connector types.
 */
export type EdgeBadgeStatus = 'live' | 'warning' | 'idle';

export interface EdgeBadge {
  status: EdgeBadgeStatus;
  label: string;
}

/**
 * Labels are supplied explicitly on every render. `StatusDot` falls back to its
 * own English labels ("Live", "Idle", "Warning") when none is passed, and those
 * would leak into a Spanish UI.
 */
const BADGES: Record<EdgeAgentState, EdgeBadge> = {
  // Green and pulsing: the agent reported within the liveness window.
  online: { status: 'live', label: 'En línea' },
  // Amber, not red: something stopped, and that is worth looking at.
  offline: { status: 'warning', label: 'Sin conexión' },
  // Grey: nothing has happened yet, which is not the same as having stopped.
  never_seen: { status: 'idle', label: 'Sin señal' },
};

/**
 * Map the server-computed agent state onto a badge.
 *
 * An unknown state degrades to "never contacted" rather than throwing: a newer
 * backend adding a state must not blank out the connectors view.
 */
export function badgeFor(state: EdgeAgentState | string | null | undefined): EdgeBadge {
  return BADGES[state as EdgeAgentState] ?? BADGES.never_seen;
}

/** The badge of one source, from whether the agent could read it on its last scan. */
export function sourceBadge(accessible: boolean): EdgeBadge {
  return accessible
    ? { status: 'live', label: 'Accesible' }
    : { status: 'warning', label: 'Sin acceso' };
}

export const EDGE_NEVER_SEEN_SENTENCE = 'Sin contacto todavía';

/**
 * Relative time for `last_seen_at`, in neutral Spanish.
 *
 * `now` is injectable so the copy is testable without freezing the clock.
 * A timestamp in the future (clock skew on either side) reads as "hace unos
 * segundos" instead of a negative number — the agent just reported.
 */
export function lastSeenSentence(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return EDGE_NEVER_SEEN_SENTENCE;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return EDGE_NEVER_SEEN_SENTENCE;

  const seconds = Math.max(0, Math.floor((now - then) / 1000));
  if (seconds < 60) return 'hace unos segundos';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return `hace ${days} d`;
}

/** Absolute-ish rendering for the last scan; same fallback shape as above. */
export function lastScanSentence(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return 'Sin escaneos todavía';
  return lastSeenSentence(iso, now);
}

const EVENT_TYPE_LABELS: Record<EdgeFileEventType, string> = {
  file_created: 'Nuevo',
  file_modified: 'Modificado',
  file_deleted: 'Eliminado',
};

export function eventTypeLabel(type: EdgeFileEventType | string): string {
  return EVENT_TYPE_LABELS[type as EdgeFileEventType] ?? type;
}

/** Byte counts for the event list. Null means the agent did not report a size. */
export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 10 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
}

/**
 * Ontology service base URL, resolved exactly as `modules/agents/
 * EvidenceDocuments.tsx` resolves it, so the file link is right in every
 * environment instead of only in the one the developer happened to run.
 */
const ONTOLOGY_API = import.meta.env.VITE_ONTOLOGY_SERVICE_URL || 'http://localhost:8004';

/**
 * Where the bytes of an ingested file actually live.
 *
 * The document record also carries `file_url` and `download_url`, and NEITHER
 * is reachable from a browser: `file_url` points into a private MinIO bucket
 * and comes back 403, and `download_url` is presigned against the internal
 * Docker host `minio:9000`, which does not resolve outside the compose
 * network. The `/documents/{id}/file` proxy is the only one that works.
 */
export function edgeDocumentFileUrl(documentId: string): string {
  return `${ONTOLOGY_API}/documents/${encodeURIComponent(documentId)}/file`;
}

/** Floor for the liveness poll, so a misconfigured cadence cannot hammer the API. */
export const EDGE_MIN_POLL_SECONDS = 30;
export const EDGE_DEFAULT_POLL_SECONDS = 60;

/**
 * How often the view may re-read liveness, in milliseconds.
 *
 * Never faster than the heartbeat cadence the server advertises: polling more
 * often than the agent reports only produces the same answer at a cost.
 */
export function pollIntervalMs(heartbeatSeconds: number | null | undefined): number {
  const seconds = Number(heartbeatSeconds);
  if (!Number.isFinite(seconds) || seconds <= 0) return EDGE_DEFAULT_POLL_SECONDS * 1000;
  return Math.max(seconds, EDGE_MIN_POLL_SECONDS) * 1000;
}

// ── Product copy ────────────────────────────────────────────────────────────

export const EDGE_DISPLAY_NAME = 'Servidor';
export const EDGE_DESCRIPTION =
  'Servicio instalado en tu servidor que vigila una carpeta y envía los archivos a PAI. PAI nunca entra a tu red: el servicio abre la conexión y se autentica con un token que PAI genera.';

export const EDGE_FORM_HINT =
  'Solo hace falta un nombre. Al crear el conector, PAI genera un token para el servicio y te lo muestra una sola vez.';

export const EDGE_TOKEN_PANEL_TITLE = 'Token del servicio';
export const EDGE_TOKEN_WARNING =
  'Copia este token ahora: no se vuelve a mostrar. Si lo pierdes, tienes que generar uno nuevo y pegarlo otra vez en el servicio.';
export const EDGE_TOKEN_SNIPPET_HINT =
  'Pega esto en el archivo edge.yaml del servicio y completa los datos de la carpeta que quieres vigilar.';
export const EDGE_TOKEN_LABEL = 'Token';
export const EDGE_SNIPPET_LABEL = 'Configuración del servicio (edge.yaml)';
export const EDGE_COPY_ACTION = 'Copiar';
export const EDGE_COPIED_LABEL = 'Copiado';
export const EDGE_TOKEN_DONE_ACTION = 'Ya lo copié';

export const EDGE_TOKEN_ACTIVE_LABEL = 'Token activo';
export const EDGE_TOKEN_INACTIVE_LABEL = 'Sin token activo';
export const EDGE_TOKEN_MISSING_HINT =
  'Este conector no tiene un token activo: el servicio no puede enviar archivos hasta que generes uno.';

export const EDGE_MINT_ACTION = 'Generar token';
export const EDGE_ROTATE_ACTION = 'Rotar token';
export const EDGE_REVOKE_ACTION = 'Revocar token';
export const EDGE_CONFIRM_ACTION = 'Confirmar';
export const EDGE_CANCEL_ACTION = 'Cancelar';

export const EDGE_ROTATE_CONFIRM =
  'Al rotar el token, el actual deja de servir de inmediato. El servicio no vuelve a enviar archivos hasta que pegues el token nuevo en su configuración.';
export const EDGE_REVOKE_CONFIRM =
  'Al revocar el token, el servicio deja de poder enviar archivos hasta que generes uno nuevo y lo pegues en su configuración.';

export const EDGE_SECTION_SERVICE = 'Estado del servicio';
export const EDGE_SECTION_TOKEN = 'Token';
export const EDGE_SECTION_EVENTS = 'Archivos reportados';

export const EDGE_LABELS = {
  state: 'Estado',
  lastSeen: 'Último contacto',
  version: 'Versión del servicio',
  sourceStatus: 'Estado del origen',
  filesObserved: 'Archivos vistos',
  pendingQueue: 'En cola',
  lastScan: 'Último escaneo',
  tokenPrefix: 'Prefijo',
} as const;

/** One row per source, shown only when the heartbeat names them. */
export const EDGE_SECTION_SOURCES = 'Orígenes';
export const EDGE_SOURCE_LABELS = {
  id: 'Origen',
  path: 'Ruta',
  state: 'Estado',
  filesSeen: 'Archivos vistos',
  lastScan: 'Último escaneo',
} as const;

export const EDGE_EVENTS_EMPTY = 'El servicio todavía no ha reportado archivos.';
export const EDGE_EVENTS_ERROR = 'No fue posible leer los archivos reportados.';

/**
 * Only rows that carry a `document_id` get this. A row without one has no file
 * to open — it is metadata, or a deletion — and says nothing at all rather than
 * explaining its own absence.
 */
export const EDGE_OPEN_FILE_ACTION = 'Abrir archivo';
export const EDGE_OPEN_FILE_ERROR = 'No fue posible abrir el archivo.';
/** A delivered file whose type must not open on PAI's origin is saved instead. */
export const EDGE_DOWNLOAD_FILE_ACTION = 'Descargar';
export const EDGE_UNKNOWN_VALUE = '—';

/** Renders a number the agent may not have reported yet. */
export function edgeCount(value: number | null | undefined): string {
  return value === null || value === undefined ? EDGE_UNKNOWN_VALUE : String(value);
}

/** Renders the opaque source status without interpreting it. */
export function sourceStatusText(value: string | null | undefined): string {
  const trimmed = (value ?? '').trim();
  return trimmed.length > 0 ? trimmed : EDGE_UNKNOWN_VALUE;
}

/**
 * The block the operator pastes into the agent's `edge.yaml`.
 *
 * `pai.url` is derived from the browser origin, so the snippet points at the
 * PAI instance the operator is actually looking at instead of a value they
 * have to guess. Identity is token-only: there is no connector id or tenant
 * key. `source.allowed_roots` is a placeholder they edit — PAI has no idea
 * what the agent watches, and does not need one.
 */
export function edgeConfigSnippet(token: string, origin: string): string {
  return [
    'pai:',
    `  url: "${origin}"`,
    `  connector_token: "${token}"`,
    'state:',
    '  directory: "/path/to/agent/state"',
    'source:',
    '  allowed_roots:',
    '    - "/path/to/watched/folder"',
    'scan_interval: 30',
    'quiet_period_seconds: 10',
    '',
  ].join('\n');
}

/** The origin the snippet should advertise, with a safe fallback off-browser. */
export function currentOrigin(): string {
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin;
  return 'https://pai.example.com';
}

// ── On-demand file channel (FETCH_FILE) ─────────────────────────────────────
//
// PAI keeps no file bytes. "Ver archivo" asks the agent for the file, waits for
// it and keeps the bytes ready for the user's click. Every answer the user can
// get is one of the outcomes below, mapped from an HTTP refusal code, a request
// status or a failure reason. Retrying is only ever an explicit user action.

export const EDGE_VIEW_FILE_ACTION = 'Ver archivo';
export const EDGE_RETRY_ACTION = 'Reintentar';
export const EDGE_VIEW_FILE_OFFLINE_REASON = 'El servicio no está en línea.';

/** Retries of a file that keeps changing, per user action, before giving up. */
export const EDGE_FETCH_MAX_RETRIES = 2;
/** Cadence of the status poll while a request is in flight. */
export const EDGE_FETCH_POLL_MS = 3000;
/**
 * Consecutive dropped polls tolerated before the wait is given up. One lost
 * answer is not a verdict: the request is still alive on the server.
 */
export const EDGE_FETCH_MAX_TRANSIENT_ERRORS = 3;

/**
 * Whether a failed call may succeed if simply asked again: the server never
 * answered, or answered that IT failed. A 4xx is a verdict about the request.
 */
export function isTransientFetchError(err: unknown): boolean {
  const status = (err as { response?: { status?: number } })?.response?.status;
  return status === undefined || status >= 500;
}

export type EdgeFetchKind =
  | 'waiting' | 'delivered' | 'offline' | 'unsupported_version' | 'file_changed'
  | 'file_changing' | 'version_unavailable' | 'outside_roots' | 'unreadable'
  | 'too_large' | 'expired' | 'cancelled' | 'not_available' | 'unsupported' | 'gone'
  | 'error';

export interface EdgeFetchOutcome {
  kind: EdgeFetchKind;
  message: string;
  /** No further polling happens once an outcome is terminal. */
  terminal: boolean;
  /** Offered as a button; never retried automatically. */
  retryable: boolean;
}

const outcome = (
  kind: EdgeFetchKind, message: string, retryable = false, terminal = true,
): EdgeFetchOutcome => ({ kind, message, terminal, retryable });

export const EDGE_FETCH_WAITING = outcome(
  'waiting', 'Esperando al conector… Puede tardar hasta un minuto.', false, false,
);
const DELIVERED = outcome('delivered', 'Archivo listo.');
const OFFLINE = outcome(
  'offline', 'El servicio no está en línea. Vuelve a intentarlo cuando se conecte.', true,
);
const UNSUPPORTED_VERSION = outcome(
  'unsupported_version', 'La versión del servicio no admite la descarga de archivos. Actualiza el servicio.',
);
const FILE_CHANGED = outcome(
  'file_changed', 'El archivo cambió y se está reindexando. Vuelve a intentarlo en unos segundos.', true,
);
const FILE_CHANGING = outcome(
  'file_changing', 'El archivo sigue cambiando. Inténtalo de nuevo más tarde.',
);
const VERSION_UNAVAILABLE = outcome(
  'version_unavailable', 'Esta versión del archivo ya no está disponible. Solo se puede ver la versión más reciente.',
);
const OUTSIDE_ROOTS = outcome(
  'outside_roots', 'El archivo está fuera de las carpetas autorizadas para el servicio.',
);
const UNREADABLE = outcome('unreadable', 'No se pudo leer el archivo en el servidor.', true);
const TOO_LARGE = outcome('too_large', 'El archivo es demasiado grande para verlo desde PAI.');
const EXPIRED = outcome('expired', 'El servicio no respondió a tiempo. Vuelve a intentarlo.', true);
// Another tab, or this one before a reload, gave the request up.
const CANCELLED = outcome('cancelled', 'La solicitud del archivo se canceló. Vuelve a intentarlo.', true);
const NOT_AVAILABLE = outcome('not_available', 'El archivo no está disponible.');
const UNSUPPORTED = outcome('unsupported', 'No se pudo procesar la solicitud del archivo.');
const GONE = outcome(
  'gone', 'El archivo ya se entregó o dejó de estar disponible. Solicítalo de nuevo.', true,
);
const GENERIC_ERROR = outcome('error', 'No se pudo obtener el archivo.', true);

/** HTTP refusal of the create call (`detail.code`). */
export function fetchRefusalOutcome(code: string | undefined): EdgeFetchOutcome {
  switch (code) {
    case 'agent_offline': return OFFLINE;
    case 'unsupported_version': return UNSUPPORTED_VERSION;
    case 'not_fetchable': return VERSION_UNAVAILABLE;
    case 'event_not_found': return NOT_AVAILABLE;
    default: return GENERIC_ERROR;
  }
}

/**
 * A failure reason reported by the agent. `hash_mismatch` is the only one that
 * may be retried repeatedly, so it alone depends on how many retries were used.
 * `not_indexed` is a record divergence: no re-index will fix it, so it must
 * never read as a file change.
 */
export function fetchOutcomeFor(reason: string | null | undefined, retries: number): EdgeFetchOutcome {
  switch (reason) {
    case 'hash_mismatch': return retries >= EDGE_FETCH_MAX_RETRIES ? FILE_CHANGING : FILE_CHANGED;
    case 'version_unavailable': return VERSION_UNAVAILABLE;
    case 'outside_allowed_roots': return OUTSIDE_ROOTS;
    case 'unreadable': return UNREADABLE;
    case 'too_large': return TOO_LARGE;
    case 'not_indexed': return NOT_AVAILABLE;
    case 'unsupported': return UNSUPPORTED;
    default: return GENERIC_ERROR;
  }
}

/** The state of a request as read from the status endpoint. */
export function fetchViewOutcome(view: EdgeFetchView, retries: number): EdgeFetchOutcome {
  switch (view.status) {
    case 'queued':
    case 'leased': return EDGE_FETCH_WAITING;
    case 'delivered': return DELIVERED;
    case 'expired': return EXPIRED;
    case 'cancelled': return CANCELLED;
    case 'failed': return fetchOutcomeFor(view.failure_reason, retries);
    default: return GENERIC_ERROR;
  }
}

/**
 * Refusal of the one-shot content read (`detail.code`). A read that got no
 * refusal code at all — a dropped connection, a 5xx — may still have consumed
 * the bytes on the server, so it is reported as gone: the honest answer is to
 * ask for the file again.
 */
export function fetchContentOutcome(code: string | undefined): EdgeFetchOutcome {
  return code === 'content_gone' || code === undefined ? GONE : GENERIC_ERROR;
}

export const EDGE_FETCH_DELIVERED = DELIVERED;
export const EDGE_FETCH_ERROR = GENERIC_ERROR;
export const EDGE_FETCH_EXPIRED = EXPIRED;
export const EDGE_FETCH_GONE = GONE;

/** Deleted files and events the agent never hashed have nothing to fetch. */
export function isEventFetchable(event: Pick<EdgeFileEvent, 'event_type' | 'content_hash'>): boolean {
  return event.event_type !== 'file_deleted' && !!event.content_hash;
}

/**
 * A file is (source, path). A file of the default source reads exactly as the
 * agent reported it; a file of any other source carries that source's id in
 * front, the way a drive letter would, so two sources holding the same
 * relative path are two different rows on screen.
 *
 * An event with no `source_id` at all comes from a backend that predates
 * sources (a replica still draining during a deploy) and is a default file.
 */
export function edgeDisplayPath(file: Pick<EdgeFileEvent, 'path'> & { source_id?: string | null }): string {
  const sourceId = file.source_id || EDGE_DEFAULT_SOURCE_ID;
  return sourceId === EDGE_DEFAULT_SOURCE_ID ? file.path : `${sourceId}:\\${file.path}`;
}

/**
 * After `hash_mismatch` the agent re-indexes and reports a NEW event for the
 * same path, so a retry has to target that one — in the same source, because
 * another source may hold the same relative path. `events` is newest first.
 */
export function latestFetchableEventId(
  events: EdgeFileEvent[], sourceId: string, path: string, fallbackId: string,
): string {
  return events.find((e) => e.source_id === sourceId && e.path === path && isEventFetchable(e))
    ?.event_id ?? fallbackId;
}

const VIEWABLE_MIME: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  txt: 'text/plain',
};

/**
 * Open in a tab or save? The server answers `application/octet-stream`, so the
 * type comes from the extension. Only inert types are opened in the browser:
 * HTML and SVG would run script from PAI's own origin.
 */
export function fetchFileDelivery(path: string): { mode: 'view' | 'download'; mime: string } {
  const ext = path.includes('.') ? path.split('.').pop()!.toLowerCase() : '';
  const mime = VIEWABLE_MIME[ext];
  return mime ? { mode: 'view', mime } : { mode: 'download', mime: 'application/octet-stream' };
}
